import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ProtectedReaderSession } from './flipbook-contracts'
import {
  FLIPBOOK_PAGE_TURN_DURATION_MS,
  FLIPBOOK_SWIPE_THRESHOLD_PX,
} from './flipbook-constants'
import { getFlipbookPerformanceDiagnostics } from './flipbook-performance-diagnostics'

export interface TurningPage {
  readonly backUrl: string | null
  readonly direction: 'next' | 'previous'
  readonly page: number
  readonly url: string
}

interface UseFlipbookNavigationOptions {
  readonly isDesktopSpread: boolean
  readonly isPageLoading: boolean
  readonly pageImages: Record<number, string>
  readonly session: ProtectedReaderSession | null
  readonly sessionResetKey: number
}

interface FlipbookNavigation {
  readonly adjacentPageNumbers: readonly number[]
  readonly currentPage: number
  readonly displayPage: number
  readonly handleKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
  readonly handleSwipe: (deltaX: number) => void
  readonly isSpreadView: boolean
  readonly navigateByStep: (direction: 'next' | 'previous') => void
  readonly navigateToPage: (page: number, direction?: 'next' | 'previous') => void
  readonly setCurrentPage: (page: number) => void
  readonly turningPage: TurningPage | null
  readonly visiblePageNumbers: readonly number[]
}

export function useFlipbookNavigation({
  isDesktopSpread,
  isPageLoading,
  pageImages,
  session,
  sessionResetKey,
}: UseFlipbookNavigationOptions): FlipbookNavigation {
  const turnTimeoutRef = useRef<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [turningPage, setTurningPage] = useState<TurningPage | null>(null)
  const isStandaloneLastPage = Boolean(session && session.pageCount % 2 === 0 && currentPage === session.pageCount)
  const isSpreadView = isDesktopSpread && currentPage > 1 && !isStandaloneLastPage
  const displayPage = session && isSpreadView && currentPage % 2 === 1
    ? Math.max(2, currentPage - 1)
    : currentPage

  const visiblePageNumbers = useMemo(() => session
    ? [displayPage, ...(isSpreadView && displayPage < session.pageCount ? [displayPage + 1] : [])]
    : [], [displayPage, isSpreadView, session])

  const adjacentPageNumbers = useMemo(() => {
    if (!session) {
      return []
    }

    return Array.from(new Set(
      (isSpreadView
        ? [displayPage - 2, displayPage - 1, displayPage + 2, displayPage + 3]
        : [displayPage - 1, displayPage + 1])
        .filter((page) => page >= 1 && page <= session.pageCount),
    ))
  }, [displayPage, isSpreadView, session])

  const navigateToPage = useCallback((page: number, direction?: 'next' | 'previous') => {
    if (!session || isPageLoading) {
      return
    }

    const requestedPage = Math.max(1, Math.min(session.pageCount, page))
    const requestedLastPageIsStandalone = session.pageCount % 2 === 0 && requestedPage === session.pageCount
    const nextPage = Math.max(1, Math.min(session.pageCount, isDesktopSpread && requestedPage > 1 && !requestedLastPageIsStandalone && requestedPage % 2 === 1 ? requestedPage - 1 : requestedPage))
    if (nextPage === displayPage) {
      return
    }

    const resolvedDirection = direction ?? (nextPage > displayPage ? 'next' : 'previous')
    const nextIsStandaloneLastPage = session.pageCount % 2 === 0 && nextPage === session.pageCount
    const nextIsSpreadView = isDesktopSpread && nextPage > 1 && !nextIsStandaloneLastPage
    const nextDisplayPage = nextIsSpreadView && nextPage % 2 === 1
      ? Math.max(2, nextPage - 1)
      : nextPage
    const targetPages = [nextDisplayPage, ...(nextIsSpreadView && nextDisplayPage < session.pageCount ? [nextDisplayPage + 1] : [])]
    const diagnostics = getFlipbookPerformanceDiagnostics()
    diagnostics.markPageTurnIntent({
      cachedPages: targetPages.filter((targetPage) => Boolean(pageImages[targetPage])),
      displayPage: displayPage,
      targetPage: nextPage,
      targetPages,
    })
    const turningPageNumber = isSpreadView
      ? resolvedDirection === 'next' ? displayPage + 1 : displayPage
      : displayPage
    const turningPageUrl = pageImages[turningPageNumber]
    // Back face shows the real adjacent page (mirrored) when already cached:
    // next → the page the sheet lands on; previous → the page before the turning one.
    const turningBackPageNumber = resolvedDirection === 'next' ? turningPageNumber + 1 : turningPageNumber - 1
    const turningBackUrl = turningBackPageNumber >= 1 && turningBackPageNumber <= session.pageCount
      ? pageImages[turningBackPageNumber] ?? null
      : null
    setTurningPage(turningPageUrl ? { backUrl: turningBackUrl, direction: resolvedDirection, page: turningPageNumber, url: turningPageUrl } : null)
    setCurrentPage(nextPage)
  }, [displayPage, isDesktopSpread, isPageLoading, isSpreadView, pageImages, session])

  const navigateByStep = useCallback((direction: 'next' | 'previous') => {
    const step = isDesktopSpread && currentPage > 1 ? 2 : 1
    navigateToPage(displayPage + (direction === 'next' ? step : -step), direction)
  }, [currentPage, displayPage, isDesktopSpread, navigateToPage])

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (!session) {
      return
    }

    // IME composition (Telex/VNI, Windows/macOS Vietnamese IME, mobile keyboards) must
    // never reach page navigation; keyCode 229 covers legacy/Android composition keys.
    if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) {
      return
    }

    // Keys originating from an editable surface (e.g. the text annotation textarea)
    // belong to that editor, not to page navigation.
    const target = event.target
    if (target instanceof HTMLElement && (target.isContentEditable || target.closest('input, textarea, select'))) {
      return
    }

    // Modifier combinations are reserved for shortcuts/OS text navigation.
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return
    }

    if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
      event.preventDefault()
      navigateByStep('previous')
    }

    if (event.key === 'ArrowRight' || event.key === 'PageDown') {
      event.preventDefault()
      navigateByStep('next')
    }
  }, [navigateByStep, session])

  const handleSwipe = useCallback((deltaX: number) => {
    if (Math.abs(deltaX) < FLIPBOOK_SWIPE_THRESHOLD_PX) {
      return
    }

    navigateByStep(deltaX > 0 ? 'previous' : 'next')
  }, [navigateByStep])

  useEffect(() => {
    if (sessionResetKey === 0) {
      return
    }

    // A new protected session represents a new reader surface; restore the initial page.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(1)
    setTurningPage(null)
  }, [sessionResetKey])

  useEffect(() => {
    if (!turningPage) {
      return
    }

    getFlipbookPerformanceDiagnostics().markPageTurnAnimationStart()
    turnTimeoutRef.current = window.setTimeout(() => {
      getFlipbookPerformanceDiagnostics().markPageTurnEnd()
      setTurningPage(null)
      turnTimeoutRef.current = null
    }, FLIPBOOK_PAGE_TURN_DURATION_MS)

    return () => {
      if (turnTimeoutRef.current !== null) {
        window.clearTimeout(turnTimeoutRef.current)
        turnTimeoutRef.current = null
      }
    }
  }, [turningPage])

  useEffect(() => () => {
    if (turnTimeoutRef.current !== null) {
      window.clearTimeout(turnTimeoutRef.current)
    }
  }, [])

  return {
    adjacentPageNumbers,
    currentPage,
    displayPage,
    handleKeyDown,
    handleSwipe,
    isSpreadView,
    navigateByStep,
    navigateToPage,
    setCurrentPage,
    turningPage,
    visiblePageNumbers,
  }
}
